import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { LogOut, Package, Calendar, AlertCircle, Plus, CheckCircle, Clock, Leaf, Droplets, Sprout, Eye, Edit2, Trash2, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { format, addDays } from 'date-fns';
import CompostingCalendar from '@/components/CompostingCalendar';
import ForecastDashboard from '@/components/ForecastDashboard';
import RoomAssignmentModal from '@/components/RoomAssignmentModal';
import TaskPhotoUpload from '@/components/TaskPhotoUpload';
import { ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CompostingSupervisorDashboard = ({ user, onLogout }) => {
  const [batches, setBatches] = useState([]);
  const [todayTasks, setTodayTasks] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [roomCycles, setRoomCycles] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBatchDetail, setShowBatchDetail] = useState(null);
  const [showEditNameModal, setShowEditNameModal] = useState(null);
  const [editBatchName, setEditBatchName] = useState('');
  const [upcomingBatches, setUpcomingBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nextPrewettingDates, setNextPrewettingDates] = useState([]);
  // Room selection modal for spawning completion (Day 20)
  const [showRoomSelectModal, setShowRoomSelectModal] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState('');
  // Assign / change room for a completed (past) batch
  const [showRoomAssignmentModal, setShowRoomAssignmentModal] = useState(null);
  // Task completion modal (with photo) for non-spawning tasks
  const [completeTaskModal, setCompleteTaskModal] = useState(null);
  const [completeNotes, setCompleteNotes] = useState('');
  const [completePhotoId, setCompletePhotoId] = useState(null);
  const [spawnPhotoId, setSpawnPhotoId] = useState(null);
  const [completing, setCompleting] = useState(false);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [batchesRes, scheduleRes, tasksRes, roomsRes, cyclesRes] = await Promise.all([
        axios.get(`${API}/compost/batches`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/compost/schedule`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/compost/today-tasks`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/rooms`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/room-cycles`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setBatches(batchesRes.data);
      setSchedule(scheduleRes.data.schedule);
      setTodayTasks(tasksRes.data.tasks);
      setRooms(roomsRes.data);
      // Handle both array and object with 'cycles' key response formats
      const cyclesData = Array.isArray(cyclesRes.data) ? cyclesRes.data : (cyclesRes.data.cycles || []);
      setRoomCycles(cyclesData);

      // Calculate upcoming batch reminders (batches nearing Day 20)
      const today = new Date();
      const upcoming = batchesRes.data.filter(batch => {
        if (batch.status !== 'active') return false;
        const start = new Date(batch.start_date);
        const daysPassed = Math.floor((today - start) / (1000 * 60 * 60 * 24)) + 1;
        return daysPassed >= 17 && daysPassed < 20;
      });
      setUpcomingBatches(upcoming);

      // Calculate next prewetting dates based on 3-day interval from last batch
      const activeBatches = batchesRes.data.filter(b => b.status === 'active');
      if (activeBatches.length > 0) {
        // Sort by start date descending to get the most recent batch
        const sortedBatches = [...activeBatches].sort((a, b) => 
          new Date(b.start_date) - new Date(a.start_date)
        );
        const lastBatch = sortedBatches[0];
        const lastStart = new Date(lastBatch.start_date);
        
        // Calculate next 3 prewetting dates (3-day intervals)
        const prewettingDates = [];
        for (let i = 1; i <= 3; i++) {
          const nextDate = addDays(lastStart, i * 3);
          if (nextDate >= today) {
            const daysUntil = Math.ceil((nextDate - today) / (1000 * 60 * 60 * 24));
            prewettingDates.push({
              date: nextDate,
              batchNumber: batchesRes.data.length + i,
              daysUntil: daysUntil
            });
          }
        }
        setNextPrewettingDates(prewettingDates);
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // Get room name for a batch (prefer the active cycle after a reassignment)
  const getRoomForBatch = (batchId) => {
    const cycle = roomCycles.find(c => c.batch_id === batchId && c.status === 'active')
      || roomCycles.find(c => c.batch_id === batchId);
    if (cycle) {
      const room = rooms.find(r => r.id === cycle.room_id);
      return room ? room.name : null;
    }
    return null;
  };

  // Get available (idle) rooms for spawning
  const getAvailableRooms = () => {
    const activeRoomIds = roomCycles
      .filter(c => c.status === 'active')
      .map(c => c.room_id);
    return rooms.filter(r => !activeRoomIds.includes(r.id));
  };

  // Handle task completion - show room modal for Day 20, photo modal otherwise
  const handleCompleteTask = async (task) => {
    if (task.day === 20) {
      // Day 20 is spawning day - need to select a room (required)
      setShowRoomSelectModal(task);
      setSelectedRoom('');
      setSpawnPhotoId(null);
    } else {
      // Open completion modal so the supervisor can attach a photo + notes
      setCompleteTaskModal(task);
      setCompleteNotes('');
      setCompletePhotoId(null);
    }
  };

  // Confirm completion of a non-spawning compost task (with optional/recommended photo)
  const handleConfirmComplete = async () => {
    const task = completeTaskModal;
    if (!task) return;
    const photoRecommended = /turning|tunnel/i.test(task.task || '');
    if (photoRecommended && !completePhotoId) {
      const ok = window.confirm(`A photo is recommended for "${task.task}". Complete without a photo?`);
      if (!ok) return;
    }
    setCompleting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/compost/complete-task`, {
        batch_id: task.batch_id,
        day: task.day,
        notes: completeNotes || null,
        photo_id: completePhotoId || null
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Task completed!');
      setCompleteTaskModal(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete task');
    } finally {
      setCompleting(false);
    }
  };

  // Complete spawning with room assignment
  const handleSpawningComplete = async () => {
    if (!selectedRoom) {
      toast.error('Please select a room');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // Complete the spawning task with room assignment
      await axios.post(`${API}/compost/complete-spawning`, {
        batch_id: showRoomSelectModal.batch_id,
        room_id: selectedRoom,
        photo_id: spawnPhotoId || null
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success('Spawning completed and room cycle started!');
      setShowRoomSelectModal(null);
      setSelectedRoom('');
      setSpawnPhotoId(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete spawning');
    }
  };

  // Delete a compost batch (e.g. a duplicate created by mistake)
  const handleDeleteBatch = async (batch) => {
    if (!window.confirm(`Delete batch "${batch.batch_name}"? This cannot be undone.`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/compost/batches/${batch.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Batch "${batch.batch_name}" deleted`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete batch');
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#F9FAF9]">
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAF9]">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-[#1A4D2E]" style={{ fontFamily: 'Outfit, sans-serif' }}>Composting Management</h1>
              <p className="text-sm text-gray-500">Composting Supervisor Dashboard</p>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">Welcome, {user.full_name}</span>
              <Button
                data-testid="logout-button"
                onClick={onLogout}
                variant="outline"
                size="sm"
                className="border-gray-300 hover:bg-gray-100"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs: Today's Tasks, Active Batches, Past Batches, Calendar */}
        <Tabs defaultValue="today" className="w-full">
          <TabsList className="mb-6 bg-white border border-gray-200">
            <TabsTrigger value="today" data-testid="today-tab">
              <Clock className="w-4 h-4 mr-2" />
              Today's Tasks
            </TabsTrigger>
            <TabsTrigger value="forecast" data-testid="forecast-tab">
              <TrendingUp className="w-4 h-4 mr-2" />
              Forecast
            </TabsTrigger>
            <TabsTrigger value="active" data-testid="active-tab">
              <Sprout className="w-4 h-4 mr-2" />
              Active Batches
            </TabsTrigger>
            <TabsTrigger value="past" data-testid="past-tab">
              <Eye className="w-4 h-4 mr-2" />
              Past Batches
            </TabsTrigger>
            <TabsTrigger value="calendar" data-testid="calendar-tab">
              <Calendar className="w-4 h-4 mr-2" />
              Calendar
            </TabsTrigger>
          </TabsList>

          {/* Forecast Tab */}
          <TabsContent value="forecast">
            <ForecastDashboard userRole="composting_supervisor" />
          </TabsContent>
          
          {/* Today's Tasks Tab */}
          <TabsContent value="today">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="border-gray-100 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <Package className="w-4 h-4" />
                Active Batches
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">
                {batches.filter(b => b.status === 'active').length}
              </div>
            </CardContent>
          </Card>

          <Card className="border-gray-100 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Today's Tasks
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">{todayTasks.length}</div>
            </CardContent>
          </Card>

          <Card className="border-yellow-200 bg-yellow-50/50 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-yellow-800 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Upcoming Spawning
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-yellow-800">{upcomingBatches.length}</div>
              <p className="text-xs text-yellow-700 mt-1">Ready in 1-3 days</p>
            </CardContent>
          </Card>

          <Card className="border-gray-100 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Completed
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">
                {batches.filter(b => b.status === 'completed').length}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Upcoming Prewetting Dates - NEW BATCH START */}
        {nextPrewettingDates.length > 0 && (
          <Card className="mb-6 border-blue-300 bg-blue-50" data-testid="prewetting-dates-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-800">
                <Droplets className="w-5 h-5" />
                Next Batch Start (Prewetting Schedule)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {nextPrewettingDates.map((item, idx) => (
                  <div key={item.batchNumber ? `pw-${item.batchNumber}` : idx} className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-200">
                    <div>
                      <p className="font-medium text-gray-900">Batch #{item.batchNumber}</p>
                      <p className="text-sm text-gray-600">Start prewetting on {format(item.date, 'EEEE, MMM d, yyyy')}</p>
                    </div>
                    <Badge className={item.daysUntil <= 1 ? "bg-red-100 text-red-800" : "bg-blue-100 text-blue-800"}>
                      {item.daysUntil === 0 ? 'Today!' : item.daysUntil === 1 ? 'Tomorrow' : `${item.daysUntil} days`}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Upcoming Spawning Alert */}
        {upcomingBatches.length > 0 && (
          <Card className="mb-6 border-yellow-300 bg-yellow-50" data-testid="upcoming-spawning-alert">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-yellow-800">
                <AlertCircle className="w-5 h-5" />
                Upcoming Spawning - Prepare Rooms!
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {upcomingBatches.map((batch) => {
                  const start = new Date(batch.start_date);
                  const spawningDate = addDays(start, 19);
                  const daysPassed = Math.floor((new Date() - start) / (1000 * 60 * 60 * 24)) + 1;
                  return (
                    <div key={batch.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-yellow-200">
                      <div>
                        <p className="font-medium text-gray-900">{batch.batch_name}</p>
                        <p className="text-sm text-gray-600">Day {daysPassed} - Spawning on {format(spawningDate, 'PP')}</p>
                      </div>
                      <Badge className="bg-yellow-100 text-yellow-800">{20 - daysPassed} days left</Badge>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Today's Tasks */}
        <Card className="mb-6" data-testid="today-tasks-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#1A4D2E]" />
                Today's Composting Tasks ({todayTasks.length})
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {todayTasks.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No composting tasks for today</p>
            ) : (
              <div className="space-y-3">
                {todayTasks.map((task, idx) => (
                  <div
                    key={task.batch_id ? `${task.batch_id}-${task.day}` : `${task.batch_name || 'task'}-${idx}`}
                    className={`p-4 rounded-lg border ${
                      task.completed ? 'bg-green-50 border-green-200'
                        : task.overdue ? 'bg-red-50 border-red-300'
                        : 'bg-white border-gray-200'
                    }`}
                    data-testid={`today-task-${idx}`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium">{task.batch_name} - Day {task.day}</p>
                          {task.completed && <CheckCircle className="w-4 h-4 text-green-600" />}
                          {!task.completed && task.overdue && (
                            <Badge className="bg-red-100 text-red-700 border border-red-300" data-testid={`overdue-badge-${idx}`}>
                              <AlertCircle className="w-3 h-3 mr-1" />
                              Overdue{task.days_overdue ? ` · ${task.days_overdue}d` : ''}
                            </Badge>
                          )}
                        </div>
                        <p className="text-lg font-semibold text-[#1A4D2E] mt-1">{task.task}</p>
                        <p className="text-sm text-gray-500 mt-1">{task.description}</p>
                        {!task.completed && task.overdue && (
                          <p className="text-xs text-red-600 mt-1">Carried forward from Day {task.day} — please complete.</p>
                        )}
                      </div>
                      {!task.completed && (
                        <Button
                          data-testid={`complete-task-${idx}`}
                          className="bg-[#1A4D2E] hover:bg-[#143D23] text-white"
                          onClick={() => handleCompleteTask(task)}
                        >
                          {task.day === 20 ? 'Complete & Assign Room' : 'Complete'}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
          </TabsContent>

          {/* Active Batches Tab */}
          <TabsContent value="active">
        <Card data-testid="active-batches-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Leaf className="w-5 h-5 text-[#1A4D2E]" />
                Active Compost Batches
              </CardTitle>
              <Button
                data-testid="create-batch-button"
                onClick={() => setShowCreateModal(true)}
                className="bg-[#1A4D2E] hover:bg-[#143D23] text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Batch
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {batches.filter(b => b.status === 'active').map((batch) => {
                const start = new Date(batch.start_date);
                const today = new Date();
                const daysPassed = Math.floor((today - start) / (1000 * 60 * 60 * 24)) + 1;
                
                return (
                  <div
                    key={batch.id}
                    className="p-4 rounded-lg border border-gray-200 hover:border-[#1A4D2E]/30 cursor-pointer transition-colors"
                    onClick={() => setShowBatchDetail(batch)}
                    data-testid={`batch-item-${batch.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-gray-900">{batch.batch_name}</p>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0 text-gray-400 hover:text-gray-600"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowEditNameModal(batch);
                              setEditBatchName(batch.batch_name);
                            }}
                            data-testid={`edit-name-btn-${batch.id}`}
                          >
                            <Edit2 className="w-3 h-3" />
                          </Button>
                        </div>
                        <p className="text-sm text-gray-500">Started: {format(start, 'PP')}</p>
                        {batch.straw_kg && (
                          <p className="text-xs text-gray-400 mt-1">
                            Straw: {batch.straw_kg}kg | Manure: {batch.chicken_manure_kg || 0}kg
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <Badge className="bg-green-100 text-green-800">Active</Badge>
                        <p className="text-sm text-gray-600 mt-1">Day {Math.min(daysPassed, 20)}/20</p>
                        <div className="w-24 bg-gray-200 rounded-full h-1.5 mt-2">
                          <div
                            className="bg-[#1A4D2E] h-1.5 rounded-full"
                            style={{ width: `${Math.min((daysPassed / 20) * 100, 100)}%` }}
                          />
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="mt-2 h-7 px-2 text-red-600 border-red-300 hover:bg-red-50"
                          onClick={(e) => { e.stopPropagation(); handleDeleteBatch(batch); }}
                          data-testid={`delete-batch-btn-${batch.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
              {batches.filter(b => b.status === 'active').length === 0 && (
                <p className="text-gray-500 text-center py-4">No active batches</p>
              )}
            </div>
          </CardContent>
        </Card>
          </TabsContent>

          {/* Past Batches Tab */}
          <TabsContent value="past">
        {batches.filter(b => b.status !== 'active').length > 0 ? (
          <Card data-testid="past-batches-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-gray-600">
                <Clock className="w-5 h-5" />
                Past Batches (Moved to Room)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {batches.filter(b => b.status !== 'active').map((batch) => {
                  const start = new Date(batch.start_date);
                  const assignedRoom = getRoomForBatch(batch.id);
                  
                  return (
                    <div
                      key={batch.id}
                      className="p-4 rounded-lg border border-gray-200 bg-gray-50 cursor-pointer hover:bg-gray-100"
                      onClick={() => setShowBatchDetail(batch)}
                      data-testid={`past-batch-${batch.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-700">{batch.batch_name}</p>
                          <p className="text-sm text-gray-500">Started: {format(start, 'PP')}</p>
                          {batch.spawning_date && (
                            <p className="text-xs text-gray-400">Spawning: {format(new Date(batch.spawning_date), 'PP')}</p>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          {assignedRoom && (
                            <Badge className="bg-blue-100 text-blue-800 border border-blue-300">
                              {assignedRoom}
                            </Badge>
                          )}
                          <Badge className="bg-gray-200 text-gray-600">
                            {batch.status === 'moved_to_room' ? 'In Room' : batch.status}
                          </Badge>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className={`w-full mt-3 ${assignedRoom ? 'text-blue-700 border-blue-600 hover:bg-blue-50' : 'text-[#1A4D2E] border-[#1A4D2E] hover:bg-[#1A4D2E]/10'}`}
                        onClick={(e) => { e.stopPropagation(); setShowRoomAssignmentModal(batch); }}
                        data-testid={`assign-room-btn-${batch.id}`}
                      >
                        <ArrowRight className="w-4 h-4 mr-2" />
                        {assignedRoom ? 'Change Room' : 'Assign Room'}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <Eye className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No past batches yet</p>
          </div>
        )}
          </TabsContent>

          <TabsContent value="calendar">
            <CompostingCalendar />
          </TabsContent>
        </Tabs>

        {/* Create Batch Modal */}
        {showCreateModal && (
          <CreateBatchModal
            onClose={() => setShowCreateModal(false)}
            onSuccess={() => {
              setShowCreateModal(false);
              fetchData();
            }}
          />
        )}

        {/* Batch Detail Modal */}
        {showBatchDetail && (
          <BatchDetailModal
            batch={showBatchDetail}
            schedule={schedule}
            onClose={() => setShowBatchDetail(null)}
          />
        )}

        {/* Assign / Change Room Modal (for completed batches) */}
        {showRoomAssignmentModal && (
          <RoomAssignmentModal
            isOpen={true}
            batch={showRoomAssignmentModal}
            onClose={() => setShowRoomAssignmentModal(null)}
            onAssignmentComplete={() => {
              setShowRoomAssignmentModal(null);
              fetchData();
            }}
          />
        )}

        {/* Complete Task Modal (with photo) for non-spawning tasks */}
        {completeTaskModal && (
          <Dialog open={true} onOpenChange={() => setCompleteTaskModal(null)}>
            <DialogContent className="sm:max-w-[500px]" data-testid="compost-complete-task-modal">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[#1A4D2E]" />
                  Complete: {completeTaskModal.task}
                </DialogTitle>
                <DialogDescription>Attach a photo and notes, then mark this composting task complete.</DialogDescription>
              </DialogHeader>
              <div className="py-2 space-y-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-600">{completeTaskModal.batch_name} — Day {completeTaskModal.day}</p>
                  <p className="text-xs text-gray-500 mt-1">{completeTaskModal.description}</p>
                </div>
                <div>
                  <Label className="mb-1 block">Photo {/turning|tunnel/i.test(completeTaskModal.task || '') ? '(recommended)' : '(optional)'}</Label>
                  <p className="text-xs text-gray-500 mb-2">Snap a picture of the compost so the Grower & Owner can see it.</p>
                  <TaskPhotoUpload onUploaded={setCompletePhotoId} testIdPrefix="compost-task" />
                </div>
                <div>
                  <Label htmlFor="compost-complete-notes">Notes (optional)</Label>
                  <Textarea
                    id="compost-complete-notes"
                    data-testid="compost-complete-notes"
                    placeholder="Any observations about this task..."
                    value={completeNotes}
                    onChange={(e) => setCompleteNotes(e.target.value)}
                  />
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <Button variant="outline" onClick={() => setCompleteTaskModal(null)}>Cancel</Button>
                  <Button
                    className="bg-[#1A4D2E] hover:bg-[#143D23] text-white"
                    onClick={handleConfirmComplete}
                    disabled={completing}
                    data-testid="confirm-compost-complete"
                  >
                    {completing ? 'Completing...' : 'Mark Complete'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Edit Batch Name Modal */}
        {showEditNameModal && (
          <EditBatchNameModal
            batch={showEditNameModal}
            onClose={() => setShowEditNameModal(null)}
            onSuccess={() => {
              setShowEditNameModal(null);
              fetchData();
            }}
          />
        )}

        {/* Room Selection Modal for Spawning Completion */}
        {showRoomSelectModal && (
          <Dialog open={true} onOpenChange={() => setShowRoomSelectModal(null)}>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Sprout className="w-5 h-5 text-green-600" />
                  Complete Spawning - Select Room
                </DialogTitle>
                <DialogDescription>Pick the growing room for this batch (required) and optionally add a spawning photo.</DialogDescription>
              </DialogHeader>
              <div className="py-4 space-y-4">
                <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                  <p className="font-medium text-green-800">
                    Batch: {showRoomSelectModal.batch_name}
                  </p>
                  <p className="text-sm text-green-600">
                    Composting complete! Select the room where this batch will be moved for growing.
                  </p>
                </div>

                <div>
                  <Label className="text-sm font-medium mb-2 block">Select Room</Label>
                  {getAvailableRooms().length === 0 ? (
                    <p className="text-red-600 text-sm">No available rooms. All rooms are currently in use.</p>
                  ) : (
                    <div className="grid grid-cols-3 gap-2">
                      {getAvailableRooms().map(room => (
                        <button
                          key={room.id}
                          type="button"
                          onClick={() => setSelectedRoom(room.id)}
                          className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                            selectedRoom === room.id
                              ? 'border-green-500 bg-green-50 text-green-800'
                              : 'border-gray-200 bg-white hover:border-green-300 hover:bg-green-50'
                          }`}
                        >
                          {room.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <Label className="text-sm font-medium mb-2 block">Photo of spawning (recommended)</Label>
                  <TaskPhotoUpload onUploaded={setSpawnPhotoId} testIdPrefix="spawn" />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setShowRoomSelectModal(null)}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="bg-green-600 hover:bg-green-700 text-white"
                    onClick={handleSpawningComplete}
                    disabled={!selectedRoom || getAvailableRooms().length === 0}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Complete Spawning
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </main>
    </div>
  );
};

const CreateBatchModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    batch_name: '',
    start_date: new Date().toISOString().split('T')[0],
    notes: '',
    straw_kg: '',
    chicken_manure_kg: '',
    gypsum_kg: '',
    urea_kg: '',
    wheat_bran_kg: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/compost/batches`, {
        batch_name: formData.batch_name,
        start_date: new Date(formData.start_date).toISOString(),
        notes: formData.notes || null,
        straw_kg: formData.straw_kg ? parseFloat(formData.straw_kg) : null,
        chicken_manure_kg: formData.chicken_manure_kg ? parseFloat(formData.chicken_manure_kg) : null,
        gypsum_kg: formData.gypsum_kg ? parseFloat(formData.gypsum_kg) : null,
        urea_kg: formData.urea_kg ? parseFloat(formData.urea_kg) : null,
        wheat_bran_kg: formData.wheat_bran_kg ? parseFloat(formData.wheat_bran_kg) : null
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success('Batch created successfully!');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create batch');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]" data-testid="create-batch-modal">
        <DialogHeader>
          <DialogTitle>Create New Compost Batch</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="batch_name">Batch Name</Label>
            <Input
              id="batch_name"
              data-testid="batch-name-input"
              value={formData.batch_name}
              onChange={(e) => setFormData({ ...formData, batch_name: e.target.value })}
              placeholder="e.g., Batch 2024-01-A"
              required
            />
          </div>

          <div>
            <Label htmlFor="start_date">Start Date</Label>
            <Input
              id="start_date"
              type="date"
              data-testid="start-date-input"
              value={formData.start_date}
              onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
              required
            />
          </div>

          <div className="border-t pt-4">
            <Label className="text-base font-medium">Raw Materials (Optional)</Label>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <Label htmlFor="straw_kg" className="text-sm">Straw (kg)</Label>
                <Input
                  id="straw_kg"
                  type="number"
                  step="0.1"
                  value={formData.straw_kg}
                  onChange={(e) => setFormData({ ...formData, straw_kg: e.target.value })}
                  placeholder="0"
                />
              </div>
              <div>
                <Label htmlFor="chicken_manure_kg" className="text-sm">Chicken Manure (kg)</Label>
                <Input
                  id="chicken_manure_kg"
                  type="number"
                  step="0.1"
                  value={formData.chicken_manure_kg}
                  onChange={(e) => setFormData({ ...formData, chicken_manure_kg: e.target.value })}
                  placeholder="0"
                />
              </div>
              <div>
                <Label htmlFor="gypsum_kg" className="text-sm">Gypsum (kg)</Label>
                <Input
                  id="gypsum_kg"
                  type="number"
                  step="0.1"
                  value={formData.gypsum_kg}
                  onChange={(e) => setFormData({ ...formData, gypsum_kg: e.target.value })}
                  placeholder="0"
                />
              </div>
              <div>
                <Label htmlFor="urea_kg" className="text-sm">Urea (kg)</Label>
                <Input
                  id="urea_kg"
                  type="number"
                  step="0.1"
                  value={formData.urea_kg}
                  onChange={(e) => setFormData({ ...formData, urea_kg: e.target.value })}
                  placeholder="0"
                />
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Any additional notes..."
              rows={2}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting} className="flex-1">
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="submit-batch-button"
              disabled={submitting}
              className="flex-1 bg-[#1A4D2E] hover:bg-[#143D23] text-white"
            >
              {submitting ? 'Creating...' : 'Create Batch'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const BatchDetailModal = ({ batch, schedule, onClose }) => {
  const start = new Date(batch.start_date);
  const today = new Date();
  const daysPassed = Math.floor((today - start) / (1000 * 60 * 60 * 24)) + 1;
  const spawningDate = addDays(start, 19);

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto" data-testid="batch-detail-modal">
        <DialogHeader>
          <DialogTitle>{batch.batch_name}</DialogTitle>
          <div className="flex gap-2 mt-2">
            <Badge className={batch.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
              {batch.status}
            </Badge>
            <span className="text-sm text-gray-500">Day {daysPassed}/20</span>
          </div>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Key Dates */}
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h3 className="font-medium text-blue-900 mb-2">Key Dates</h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-gray-600">Start:</span>
                <span className="ml-2 font-medium">{format(start, 'PP')}</span>
              </div>
              <div>
                <span className="text-gray-600">Spawning Ready:</span>
                <span className="ml-2 font-medium">{format(spawningDate, 'PP')}</span>
              </div>
            </div>
          </div>

          {/* Raw Materials */}
          {(batch.straw_kg || batch.chicken_manure_kg) && (
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <h3 className="font-medium text-gray-900 mb-2">Raw Materials</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {batch.straw_kg && <div>Straw: {batch.straw_kg} kg</div>}
                {batch.chicken_manure_kg && <div>Chicken Manure: {batch.chicken_manure_kg} kg</div>}
                {batch.gypsum_kg && <div>Gypsum: {batch.gypsum_kg} kg</div>}
                {batch.urea_kg && <div>Urea: {batch.urea_kg} kg</div>}
                {batch.wheat_bran_kg && <div>Wheat Bran: {batch.wheat_bran_kg} kg</div>}
              </div>
            </div>
          )}

          {/* Schedule Preview */}
          <div>
            <h3 className="font-medium mb-2">20-Day Schedule</h3>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {schedule.slice(0, 20).map((day) => {
                const taskDate = addDays(start, day.day - 1);
                const isToday = format(today, 'yyyy-MM-dd') === format(taskDate, 'yyyy-MM-dd');
                const isPast = taskDate < today;
                
                return (
                  <div
                    key={day.day}
                    className={`p-2 rounded text-sm flex justify-between ${
                      isToday ? 'bg-blue-100 border border-blue-300' :
                      isPast ? 'bg-gray-100' : 'bg-white border border-gray-200'
                    }`}
                  >
                    <span>Day {day.day}: {day.task}</span>
                    <span className="text-gray-500">{format(taskDate, 'MMM d')}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Edit Batch Name Modal
const EditBatchNameModal = ({ batch, onClose, onSuccess }) => {
  const [batchName, setBatchName] = useState(batch.batch_name);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!batchName.trim()) {
      toast.error('Please enter a batch name');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/compost/batches/${batch.id}/update-name`,
        { batch_name: batchName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Batch name updated');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update batch name');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>Edit Batch Name</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="edit-batch-name">Batch Name</Label>
            <Input
              id="edit-batch-name"
              data-testid="edit-batch-name-input"
              value={batchName}
              onChange={(e) => setBatchName(e.target.value)}
              placeholder="Enter new batch name"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              className="bg-[#1A4D2E] hover:bg-[#1A4D2E]/90"
              onClick={handleSave}
              disabled={saving}
              data-testid="save-batch-name-btn"
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CompostingSupervisorDashboard;
